import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";
import type {
  ChecklistTemplateDto,
  CreateChecklistTemplateBody,
  CreateExitChecklistTaskBody,
  ExitActionBody,
  ExitWorkflowDecisionApi,
  ExitAttachmentUploadResult,
  SuggestExitLastWorkingDayBody,
  ExitChecklistTaskDto,
  ExitCompletionGatesDto,
  ExitCompletionRequirementsDto,
  ExitInterviewQuestionSetDto,
  ExitLifecycleDetailDto,
  ExitPendingListPayload,
  ExitSettlementDto,
  MyExit,
  PatchExitChecklistTaskBody,
  PatchExitInterviewSettingsBody,
  PutExitSettlementBody,
  ResignationFormContext,
  SubmitResignationBody,
} from "../../components/separation/Utils/types";
import { toExitWorkflowDecisionApi } from "../../components/separation/Utils/types";
import { normalizeExitPendingListPayload } from "../../components/separation/Utils/exitListNormalize";

const EXIT_PREFIX = "/v2/exit";

/** GET /v2/exit/pending-approvals — optional scope and org (SuperAdmin + scope=all). */
export type ExitPendingApprovalsQueryArg = {
  scope?: "pending" | "all" | "history";
  organizationId?: string;
};

function unwrapExitPayload<T>(raw: unknown): T {
  if (raw === null || raw === undefined) return raw as T;
  if (
    typeof raw === "object" &&
    raw !== null &&
    "data" in raw &&
    "success" in raw
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function pickDefinedBool(...candidates: unknown[]): boolean | undefined {
  for (const v of candidates) {
    if (v === true || v === "true" || v === 1 || v === "1") return true;
    if (v === false || v === "false" || v === 0 || v === "0") return false;
  }
  return undefined;
}

/**
 * GET/PATCH lifecycle payloads may expose interview self-submit under snake_case,
 * nested `settings`, or alternate keys — normalize so the UI toggle stays in sync.
 */
function normalizeExitInterviewSelfSubmitInLifecycle(
  dto: ExitLifecycleDetailDto,
): ExitLifecycleDetailDto {
  const iv = dto.exitInterview;
  if (iv == null) return dto;
  const rec = iv as Record<string, unknown>;
  const settings =
    rec.settings && typeof rec.settings === "object" && rec.settings !== null
      ? (rec.settings as Record<string, unknown>)
      : undefined;

  const resolved = pickDefinedBool(
    iv.employeeSelfSubmitEnabled,
    rec.employee_self_submit_enabled,
    rec.employeeSelfSubmit,
    settings?.employeeSelfSubmitEnabled,
    settings?.employee_self_submit_enabled,
    settings?.employeeSelfSubmit,
  );

  if (resolved === undefined) return dto;

  return {
    ...dto,
    exitInterview: {
      ...iv,
      employeeSelfSubmitEnabled: resolved,
    },
  };
}

/** Some checklist rows include assignee name under alternate keys; normalize for the UI. */
function normalizeChecklistTasksInLifecycle(
  dto: ExitLifecycleDetailDto,
): ExitLifecycleDetailDto {
  const tasks = dto.checklistTasks;
  if (!tasks?.length) return dto;
  const next: ExitChecklistTaskDto[] = tasks.map((t) => {
    if (t.assignedToName && String(t.assignedToName).trim()) return t;
    const r = t as unknown as Record<string, unknown>;
    const fromSnake = r.assigned_to_name;
    const fromAlt = r.assigneeName;
    const assigneeObj = r.assignee;
    const fromAssigneeUser =
      assigneeObj && typeof assigneeObj === "object" && assigneeObj !== null
        ? (assigneeObj as { fullName?: string; name?: string })
        : null;
    const name =
      (typeof fromSnake === "string" && fromSnake.trim()) ||
      (typeof fromAlt === "string" && fromAlt.trim()) ||
      (fromAssigneeUser?.fullName && String(fromAssigneeUser.fullName).trim()) ||
      (fromAssigneeUser?.name && String(fromAssigneeUser.name).trim());
    if (name) return { ...t, assignedToName: name };
    return t;
  });
  return { ...dto, checklistTasks: next };
}

function transformExitLifecycleResponse(raw: unknown): ExitLifecycleDetailDto {
  const dto = unwrapExitPayload<ExitLifecycleDetailDto>(raw);
  return normalizeChecklistTasksInLifecycle(
    normalizeExitInterviewSelfSubmitInLifecycle(dto),
  );
}

/** Backend may return one exit or a history array; UI always works with a list. */
function normalizeMyExitListPayload(raw: unknown): MyExit[] {
  const inner = unwrapExitPayload<MyExit | MyExit[] | null | undefined>(raw);
  if (inner == null) return [];
  if (Array.isArray(inner)) return inner;
  return [inner];
}

/** Arg for checklist templates list (optional department filter). */
type ExitChecklistTemplatesQueryArg = { department?: string } | void;

function lifecycleTags(exitId: string) {
  return [{ type: "ExitLifecycle" as const, id: exitId }];
}

/** Backend expects lowercase decision (pending, approved, rejected, …). */
function normalizeExitActionPayload(
  body: ExitActionBody
): ExitActionBody & { decision: ExitWorkflowDecisionApi } {
  return { ...body, decision: toExitWorkflowDecisionApi(body.decision) };
}

export const exitApi = createApi({
  reducerPath: "exitApi",
  baseQuery,
  tagTypes: [
    "ExitForm",
    "MyExit",
    "ExitApprovals",
    "ExitLifecycle",
    "ExitTemplates",
    "ExitInterviewSets",
  ],
  endpoints: (builder) => ({
    getResignationForm: builder.query<ResignationFormContext, void>({
      query: () => `${EXIT_PREFIX}/resignation-form`,
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ResignationFormContext>(raw),
      providesTags: ["ExitForm"],
    }),

    getMyExit: builder.query<MyExit[], void>({
      query: () => `${EXIT_PREFIX}/my-exit`,
      transformResponse: (raw: unknown) => normalizeMyExitListPayload(raw),
      providesTags: ["MyExit"],
    }),

    submitResignation: builder.mutation<MyExit, SubmitResignationBody>({
      query: (body) => ({
        url: `${EXIT_PREFIX}/resign`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: unknown) => unwrapExitPayload<MyExit>(raw),
      invalidatesTags: ["MyExit", "ExitForm", "ExitApprovals"],
    }),

    uploadExitAttachment: builder.mutation<
      ExitAttachmentUploadResult,
      File
    >({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `${EXIT_PREFIX}/upload-attachment`,
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ExitAttachmentUploadResult>(raw),
    }),

    withdrawExit: builder.mutation<MyExit | void, string>({
      query: (id) => ({
        url: `${EXIT_PREFIX}/${id}/withdraw`,
        method: "POST",
      }),
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<MyExit | void>(raw),
      invalidatesTags: ["MyExit", "ExitForm"],
    }),

    /**
     * GET /v2/exit/pending-approvals
     * - Default / scope=pending: workflow queue for the current user.
     * - scope=all: org or hierarchy-wide list (backend rules); SuperAdmin without token org must pass organizationId.
     */
    getExitPendingApprovals: builder.query<
      ExitPendingListPayload,
      ExitPendingApprovalsQueryArg | void
    >({
      query: (arg) => {
        const search = new URLSearchParams();
        if (arg?.scope) search.set("scope", arg.scope);
        if (arg?.organizationId) search.set("organizationId", arg.organizationId);
        const qs = search.toString();
        return `${EXIT_PREFIX}/pending-approvals${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (raw: unknown) =>
        normalizeExitPendingListPayload(raw),
      providesTags: ["ExitApprovals"],
    }),

    exitAction: builder.mutation<
      MyExit,
      { id: string; body: ExitActionBody }
    >({
      query: ({ id, body }) => ({
        url: `${EXIT_PREFIX}/${id}/action`,
        method: "PATCH",
        body: normalizeExitActionPayload(body),
      }),
      transformResponse: (raw: unknown) => unwrapExitPayload<MyExit>(raw),
      invalidatesTags: ["ExitApprovals", "MyExit", "ExitForm"],
    }),

    suggestExitLastWorkingDay: builder.mutation<
      MyExit,
      { id: string; body: SuggestExitLastWorkingDayBody }
    >({
      query: ({ id, body }) => ({
        url: `${EXIT_PREFIX}/${id}/manager/suggest-last-day`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: unknown) => unwrapExitPayload<MyExit>(raw),
      invalidatesTags: ["ExitApprovals", "MyExit", "ExitForm"],
    }),

    getExitLifecycle: builder.query<ExitLifecycleDetailDto, string>({
      query: (exitId) => `${EXIT_PREFIX}/${exitId}/lifecycle`,
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      providesTags: (_r, _e, exitId) => lifecycleTags(exitId),
    }),

    getExitCompletionGates: builder.query<ExitCompletionGatesDto, string>({
      query: (exitId) => `${EXIT_PREFIX}/${exitId}/completion-gates`,
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ExitCompletionGatesDto>(raw),
      providesTags: (_r, _e, exitId) => lifecycleTags(exitId),
    }),

    patchExitCompletionRequirements: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; body: Partial<ExitCompletionRequirementsDto> }
    >({
      query: ({ exitId, body }) => ({
        url: `${EXIT_PREFIX}/${exitId}/completion-requirements`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      async onQueryStarted(
        { exitId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            exitApi.util.updateQueryData("getExitLifecycle", exitId, (draft) => {
              Object.assign(draft, data);
            }),
          );
          if (data.completionGatesPreview) {
            dispatch(
              exitApi.util.updateQueryData(
                "getExitCompletionGates",
                exitId,
                (draft) => {
                  Object.assign(draft, data.completionGatesPreview!);
                },
              ),
            );
          }
        } catch {
          /* mutation error — no cache update */
        }
      },
      /** Avoid invalidating ExitLifecycle: refetch was slow; cache is updated in onQueryStarted. */
      invalidatesTags: () => ["MyExit"],
    }),

    startExitInProgress: builder.mutation<ExitLifecycleDetailDto, string>({
      query: (exitId) => ({
        url: `${EXIT_PREFIX}/${exitId}/lifecycle/start-in-progress`,
        method: "POST",
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, exitId) => [...lifecycleTags(exitId), "MyExit"],
    }),

    moveExitToFinalSettlement: builder.mutation<ExitLifecycleDetailDto, string>(
      {
        query: (exitId) => ({
          url: `${EXIT_PREFIX}/${exitId}/lifecycle/move-to-final-settlement`,
          method: "POST",
        }),
        transformResponse: (raw: unknown) =>
          transformExitLifecycleResponse(raw),
        invalidatesTags: (_r, _e, exitId) => [
          ...lifecycleTags(exitId),
          "MyExit",
        ],
      },
    ),

    completeExitLifecycle: builder.mutation<ExitLifecycleDetailDto, string>({
      query: (exitId) => ({
        url: `${EXIT_PREFIX}/${exitId}/lifecycle/complete-exit`,
        method: "POST",
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, exitId) => [...lifecycleTags(exitId), "MyExit"],
    }),

    getExitChecklistTemplates: builder.query<
      ChecklistTemplateDto[],
      ExitChecklistTemplatesQueryArg
    >({
      query: (arg) => {
        const dept =
          arg && typeof arg === "object" && "department" in arg
            ? arg.department
            : undefined;
        const s = new URLSearchParams();
        if (dept) s.set("department", dept);
        const qs = s.toString();
        return `${EXIT_PREFIX}/checklist-templates${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ChecklistTemplateDto[]>(raw),
      providesTags: ["ExitTemplates"],
    }),

    createExitChecklistTemplate: builder.mutation<
      ChecklistTemplateDto,
      CreateChecklistTemplateBody
    >({
      query: (body) => ({
        url: `${EXIT_PREFIX}/checklist-templates`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ChecklistTemplateDto>(raw),
      invalidatesTags: ["ExitTemplates"],
    }),

    updateExitChecklistTemplate: builder.mutation<
      ChecklistTemplateDto,
      { templateId: string; body: Partial<CreateChecklistTemplateBody> }
    >({
      query: ({ templateId, body }) => ({
        url: `${EXIT_PREFIX}/checklist-templates/${templateId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ChecklistTemplateDto>(raw),
      invalidatesTags: ["ExitTemplates"],
    }),

    deleteExitChecklistTemplate: builder.mutation<void, string>({
      query: (templateId) => ({
        url: `${EXIT_PREFIX}/checklist-templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ExitTemplates"],
    }),

    applyExitChecklistTemplate: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; templateId: string }
    >({
      query: ({ exitId, templateId }) => ({
        url: `${EXIT_PREFIX}/${exitId}/checklist/apply-template`,
        method: "POST",
        body: { templateId },
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    createExitChecklistTask: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; body: CreateExitChecklistTaskBody }
    >({
      query: ({ exitId, body }) => ({
        url: `${EXIT_PREFIX}/${exitId}/checklist/tasks`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    updateExitChecklistTask: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; taskId: string; body: PatchExitChecklistTaskBody }
    >({
      query: ({ exitId, taskId, body }) => ({
        url: `${EXIT_PREFIX}/${exitId}/checklist/tasks/${taskId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    completeExitChecklistTask: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; taskId: string; comments?: string }
    >({
      query: ({ exitId, taskId, comments }) => ({
        url: `${EXIT_PREFIX}/${exitId}/checklist/tasks/${taskId}/complete`,
        method: "POST",
        body: comments ? { comments } : {},
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    waiveExitChecklistTask: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; taskId: string; reason: string }
    >({
      query: ({ exitId, taskId, reason }) => ({
        url: `${EXIT_PREFIX}/${exitId}/checklist/tasks/${taskId}/waive`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    deleteExitChecklistTask: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; taskId: string }
    >({
      query: ({ exitId, taskId }) => ({
        url: `${EXIT_PREFIX}/${exitId}/checklist/tasks/${taskId}`,
        method: "DELETE",
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    getExitInterviewQuestionSets: builder.query<
      ExitInterviewQuestionSetDto[],
      void
    >({
      query: () => `${EXIT_PREFIX}/interview-question-sets`,
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ExitInterviewQuestionSetDto[]>(raw),
      providesTags: ["ExitInterviewSets"],
    }),

    createExitInterviewQuestionSet: builder.mutation<
      ExitInterviewQuestionSetDto,
      Record<string, unknown>
    >({
      query: (body) => ({
        url: `${EXIT_PREFIX}/interview-question-sets`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ExitInterviewQuestionSetDto>(raw),
      invalidatesTags: ["ExitInterviewSets"],
    }),

    updateExitInterviewQuestionSet: builder.mutation<
      ExitInterviewQuestionSetDto,
      { setId: string; body: Record<string, unknown> }
    >({
      query: ({ setId, body }) => ({
        url: `${EXIT_PREFIX}/interview-question-sets/${setId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ExitInterviewQuestionSetDto>(raw),
      invalidatesTags: ["ExitInterviewSets"],
    }),

    deleteExitInterviewQuestionSet: builder.mutation<void, string>({
      query: (setId) => ({
        url: `${EXIT_PREFIX}/interview-question-sets/${setId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ExitInterviewSets"],
    }),

    applyExitInterviewQuestionSet: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; questionSetId: string }
    >({
      query: ({ exitId, questionSetId }) => ({
        url: `${EXIT_PREFIX}/${exitId}/interview/apply-question-set`,
        method: "POST",
        body: { questionSetId },
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    patchExitInterviewSettings: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; body: PatchExitInterviewSettingsBody }
    >({
      query: ({ exitId, body }) => ({
        url: `${EXIT_PREFIX}/${exitId}/interview/settings`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      async onQueryStarted({ exitId, body }, { dispatch, queryFulfilled }) {
        if (typeof body.employeeSelfSubmitEnabled !== "boolean") return;
        const next = body.employeeSelfSubmitEnabled;
        const patchResult = dispatch(
          exitApi.util.updateQueryData("getExitLifecycle", exitId, (draft) => {
            if (!draft.exitInterview) return;
            draft.exitInterview.employeeSelfSubmitEnabled = next;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    patchExitInterviewResponses: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; responses: { questionKey: string; value: string }[] }
    >({
      query: ({ exitId, responses }) => ({
        url: `${EXIT_PREFIX}/${exitId}/interview/responses`,
        method: "PATCH",
        body: { responses },
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [...lifecycleTags(exitId)],
    }),

    submitExitInterview: builder.mutation<ExitLifecycleDetailDto, string>({
      query: (exitId) => ({
        url: `${EXIT_PREFIX}/${exitId}/interview/submit`,
        method: "POST",
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, exitId) => [...lifecycleTags(exitId), "MyExit"],
    }),

    getExitSettlement: builder.query<ExitSettlementDto, string>({
      query: (exitId) => `${EXIT_PREFIX}/${exitId}/settlement`,
      transformResponse: (raw: unknown) =>
        unwrapExitPayload<ExitSettlementDto>(raw),
      providesTags: (_r, _e, exitId) => lifecycleTags(exitId),
    }),

    putExitSettlement: builder.mutation<
      ExitLifecycleDetailDto,
      { exitId: string; body: PutExitSettlementBody }
    >({
      query: ({ exitId, body }) => ({
        url: `${EXIT_PREFIX}/${exitId}/settlement`,
        method: "PUT",
        body,
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, { exitId }) => [
        ...lifecycleTags(exitId),
        "MyExit",
      ],
    }),

    completeExitSettlement: builder.mutation<ExitLifecycleDetailDto, string>({
      query: (exitId) => ({
        url: `${EXIT_PREFIX}/${exitId}/settlement/complete`,
        method: "POST",
      }),
      transformResponse: (raw: unknown) =>
        transformExitLifecycleResponse(raw),
      invalidatesTags: (_r, _e, exitId) => [...lifecycleTags(exitId), "MyExit"],
    }),
  }),
});

export const {
  useGetResignationFormQuery,
  useGetMyExitQuery,
  useSubmitResignationMutation,
  useUploadExitAttachmentMutation,
  useWithdrawExitMutation,
  useGetExitPendingApprovalsQuery,
  useExitActionMutation,
  useSuggestExitLastWorkingDayMutation,
  useGetExitLifecycleQuery,
  useLazyGetExitLifecycleQuery,
  useGetExitCompletionGatesQuery,
  usePatchExitCompletionRequirementsMutation,
  useStartExitInProgressMutation,
  useMoveExitToFinalSettlementMutation,
  useCompleteExitLifecycleMutation,
  useGetExitChecklistTemplatesQuery,
  useCreateExitChecklistTemplateMutation,
  useUpdateExitChecklistTemplateMutation,
  useDeleteExitChecklistTemplateMutation,
  useApplyExitChecklistTemplateMutation,
  useCreateExitChecklistTaskMutation,
  useUpdateExitChecklistTaskMutation,
  useCompleteExitChecklistTaskMutation,
  useWaiveExitChecklistTaskMutation,
  useDeleteExitChecklistTaskMutation,
  useGetExitInterviewQuestionSetsQuery,
  useCreateExitInterviewQuestionSetMutation,
  useUpdateExitInterviewQuestionSetMutation,
  useDeleteExitInterviewQuestionSetMutation,
  useApplyExitInterviewQuestionSetMutation,
  usePatchExitInterviewSettingsMutation,
  usePatchExitInterviewResponsesMutation,
  useSubmitExitInterviewMutation,
  useGetExitSettlementQuery,
  usePutExitSettlementMutation,
  useCompleteExitSettlementMutation,
} = exitApi;
